import axios from "axios";
import { sendFile, sendPatch } from "./utils";
import { parseDateOnly, toDateOnly } from "../utils";
import type { OrganizationId } from "./organizations";
import type { ProjectConfigurationId, ProjectRoleId } from "./projectConfigs";
import type { TeamId } from "./teams";
import type { UserId } from "./users";
import { PriorityId, TicketKey, TicketStatusId, TicketStatusStageId } from "./tickets";

export enum ProjectPermission {
    /** Administrator access to a project. */
    ProjectAdministrator = 0,
    /** Browse a project and its tickets. */
    CanViewProject = 1,
    /** Create, edit, and delete milestones and versions. */
    CanEditVersions = 2,
    /** Can be assigned to tickets. */
    CanBeAssignedTickets = 3,
    /** Can Reassign tickets to others. */
    CanReassignTickets = 4,
    /** Create new tickets. */
    CanCreateTickets = 5,
    /** Delete tickets. */
    CanDeleteTickets = 6,
    /** Can set ticket statuses to unresolved statuses. */
    CanChangeTicketStatus = 7,
    /** Can set ticket statuses to resolved. */
    CanResolveTickets = 8,
    /** Can change the priority of tickets. */
    CanChangeTicketPriority = 9,
    /** Can change the type of tickets. */
    CanChangeTicketType = 10,
    /** Can edit the descriptions, titles, and labels of tickets. */
    CanEditTicketDescription = 11,
    /** Create and edit ticket labels. */
    CanManageLabels = 12,
    /** Link tickets to other tickets. */
    CanLinkTickets = 13,
    /** Set ticket affect and fix versions. */
    CanSetTicketVersions = 14,
    /** Upload attachments to tickets. */
    CanUploadAttachments = 15,
    /** Delete ticket attachments that other users uploaded. */
    CanDeleteAttachments = 16,
    /** Comment on tickets. */
    CanComment = 17,
    /** Delete comments that other users made. */
    CanDeleteComments = 18,
    /** Edit comments that other users made. */
    CanEditComments = 19,
    /** View the edit history of comments */
    CanViewCommentHistory = 20,
    /** Can report on Tickets */
    CanReportOnTickets = 21,
    /** Comment or edit comments on resolved tickets **/
    CanCommentOnClosedTickets = 22,
    /** Log time on tickets */
    CanLogTime = 23,
    /** Delete work log entries that other users made */
    CanManageWorkLogs = 24,
}

export type ProjectId = string & { __brand: "ProjectId" };
export type ProjectKey = string & { __brand: "ProjectKey" };
export type Project = {
    id: ProjectId;
    key: ProjectKey;
    name: string;
    organizationId: OrganizationId;
    configurationId: ProjectConfigurationId;
};

export type ProjectEdit = Pick<Project, "name">;

export type ProjectPriorityCount = {
    priorityId: PriorityId;
    name: string;
    iconColor: string | null;
    order: number;
    count: number;
};

export type ProjectNextVersion = {
    id: ProjectVersionId;
    name: string;
    endDate: string | null;
    freezeDate: string | null;
    done: number;
    total: number;
};

/** Aggregate counts behind the projects page; one round trip for every visible project. */
export type ProjectSummary = {
    id: ProjectId;
    key: ProjectKey;
    name: string;
    organizationId: OrganizationId;
    organizationName: string;
    mine: number;
    open: number;
    overdue: number;
    priorities: ProjectPriorityCount[];
    nextVersion: ProjectNextVersion | null;
};

export type MilestoneId = string & { __brand: "MilestoneId" };
export type Milestone = {
    id: MilestoneId;
    name: string;
    startDate: Date | null;
    endDate: Date | null;
};

export type MilestoneEdit = Omit<
    Pick<Milestone, "name" | "startDate" | "endDate">,
    "startDate" | "endDate"
> & {
    name: string;
    startDate: string | null;
    endDate: string | null;
};

export type ProjectTeam = {
    teamId: TeamId;
    organizationId: OrganizationId;
    permissions: ProjectPermission[];
};

export enum ProjectAccessScope {
    Administrator = 0,
    Project = 1,
    AssignedProjects = 2,
    OrganizationProjects = 3,
}

export type ProjectAccessSource = {
    scope: ProjectAccessScope;
    teamId: TeamId | null;
    teamName: string | null;
};

export type ProjectUser = {
    id: UserId;
    permissions: ProjectPermission[];
    sources: ProjectAccessSource[];
};

export type ProjectVersionId = string & { __brand: "ProjectVersionId" };

/** A "missed date" record, written when an already-arrived end date was moved. */
export type ProjectVersionSlip = {
    fromDate: Date;
    toDate: Date | null;
    slippedAt: Date;
};

export type ProjectRoleHolders = {
    id: ProjectRoleId;
    name: string;
    userIds: UserId[];
    teamIds: TeamId[];
    /** Statuses whose routing rule sends tickets to this role. */
    routedFromStatusIds: TicketStatusId[];
};

export type ProjectRoleHoldersEdit = {
    roleId: ProjectRoleId;
    userIds: UserId[];
    teamIds: TeamId[];
};

export type ProjectVersionStage = {
    id: TicketStatusStageId;
    name: string;
    color: string | null;
    statusIds: TicketStatusId[];
    count: number;
};

export type ProjectVersionUnstagedStatus = {
    id: TicketStatusId;
    name: string;
};

export type ProjectVersion = {
    id: ProjectVersionId;
    name: string;
    startDate: Date | null;
    endDate: Date | null;
    freezeDate: Date | null;
    releaseDate: Date | null;
    unresolvedTickets: TicketKey[] | null;
    totalTickets: number;
    /** In stage order, every stage of the project's configuration; empty when it has none. */
    stages: ProjectVersionStage[];
    /** The configuration's statuses that belong to no stage; tickets in them are the unstaged count. */
    unstagedStatuses: ProjectVersionUnstagedStatus[];
    slips: ProjectVersionSlip[];
    theme: string | null;
    notes: string | null;
    hiddenFromRadar: boolean;
};

export type ProjectVersionEdit = Pick<
    ProjectVersionResponse,
    | "name"
    | "startDate"
    | "endDate"
    | "freezeDate"
    | "releaseDate"
    | "theme"
    | "notes"
    | "hiddenFromRadar"
>;

export type ProjectVersionReleaseModel = Pick<ProjectVersionResponse, "releaseDate" | "notes"> & {
    migrateTickets: boolean;
    migrateTo: ProjectVersionId[];
};

type MilestoneResponse = Omit<Milestone, "startDate" | "endDate"> & {
    startDate: string | null;
    endDate: string | null;
};

type ProjectVersionSlipResponse = {
    fromDate: string;
    toDate: string | null;
    slippedAt: string;
};

type ProjectVersionResponse = Omit<
    ProjectVersion,
    | "startDate"
    | "endDate"
    | "freezeDate"
    | "releaseDate"
    | "totalTickets"
    | "stages"
    | "unstagedStatuses"
    | "slips"
    | "hiddenFromRadar"
> & {
    startDate: string | null;
    endDate: string | null;
    freezeDate: string | null;
    releaseDate: string | null;
    // Only populated by the list endpoint; absent on create.
    totalTickets?: number;
    stages?: ProjectVersionStage[];
    unstagedStatuses?: ProjectVersionUnstagedStatus[];
    slips?: ProjectVersionSlipResponse[];
    hiddenFromRadar?: boolean;
    notes: string | null;
};

function formatMilestone(milestone: MilestoneResponse): Milestone {
    return {
        ...milestone,
        startDate: milestone.startDate ? new Date(milestone.startDate) : null,
        endDate: milestone.endDate ? new Date(milestone.endDate) : null,
    };
}

function formatVersion(version: ProjectVersionResponse): ProjectVersion {
    return {
        ...version,
        startDate: version.startDate ? parseDateOnly(version.startDate) : null,
        endDate: version.endDate ? parseDateOnly(version.endDate) : null,
        freezeDate: version.freezeDate ? parseDateOnly(version.freezeDate) : null,
        releaseDate: version.releaseDate ? parseDateOnly(version.releaseDate) : null,
        totalTickets: version.totalTickets ?? 0,
        stages: version.stages ?? [],
        unstagedStatuses: version.unstagedStatuses ?? [],
        slips: (version.slips ?? []).map((slip) => ({
            fromDate: parseDateOnly(slip.fromDate),
            toDate: slip.toDate ? parseDateOnly(slip.toDate) : null,
            // slippedAt is a real timestamp (when the miss was recorded), not a planning day.
            slippedAt: new Date(slip.slippedAt),
        })),
        hiddenFromRadar: version.hiddenFromRadar ?? false,
    };
}

export type ProjectApi = {
    keyAvailable(key: string): Promise<boolean>;

    getProjects(): Promise<Project[]>;
    getProjectSummaries(): Promise<ProjectSummary[]>;

    changeOrganization(project: ProjectId, organization: OrganizationId): Promise<void>;

    editProject(project: ProjectId, model: ProjectEdit): Promise<void>;

    deleteProject(project: ProjectId): Promise<void>;

    getUsers(id: ProjectId): Promise<ProjectUser[]>;

    getMilestones(id: ProjectId): Promise<Milestone[]>;
    createMilestone(
        id: ProjectId,
        name: string,
        startDate?: Date,
        endDate?: Date
    ): Promise<Milestone>;
    editMilestone(
        project: ProjectId,
        milestone: Milestone,
        patch: Partial<MilestoneEdit>
    ): Promise<void>;
    deleteMilestone(project: ProjectId, milestone: MilestoneId): Promise<void>;

    getTeams(id: ProjectId): Promise<ProjectTeam[]>;
    addTeam(project: ProjectId, team: TeamId): Promise<void>;
    removeTeam(project: ProjectId, team: TeamId): Promise<void>;
    setTeamPermissions(
        project: ProjectId,
        team: TeamId,
        permissions: ProjectPermission[]
    ): Promise<void>;

    getVersions(id: ProjectId): Promise<ProjectVersion[]>;
    getRoleHolders(id: ProjectId): Promise<ProjectRoleHolders[]>;
    setRoleHolders(id: ProjectId, holders: ProjectRoleHoldersEdit[]): Promise<ProjectRoleHolders[]>;
    createVersion(
        id: ProjectId,
        name: string,
        startDate?: Date,
        endDate?: Date,
        freezeDate?: Date
    ): Promise<ProjectVersion>;
    editVersion(
        project: ProjectId,
        version: ProjectVersion,
        patch: Partial<ProjectVersionEdit>
    ): Promise<void>;
    setVersionHiddenFromRadar(
        project: ProjectId,
        version: ProjectVersionId,
        hidden: boolean
    ): Promise<void>;
    releaseVersion(
        Project: ProjectId,
        version: ProjectVersionId,
        patch: ProjectVersionReleaseModel
    ): Promise<void>;
    deleteVersion(project: ProjectId, version: ProjectVersionId): Promise<void>;

    importTickets(
        project: ProjectId,
        file: File
    ): Promise<{ epicsCreated: number; ticketsCreated: number }>;
};

export const projectApi: ProjectApi = {
    async keyAvailable(key) {
        const { data } = await axios.get<{ available: boolean }>(
            `/api/v0/projects/key-available?key=${key}`
        );

        return data.available;
    },

    async getProjects() {
        const { data: projects } = await axios.get<Project[]>(`/api/v0/projects`);

        return projects;
    },

    async getProjectSummaries() {
        const { data } = await axios.get<ProjectSummary[]>(`/api/v0/projects/summary`);

        return data;
    },

    async changeOrganization(project, organization) {
        await axios.post(`/api/v0/projects/${project}/change-organization/${organization}`);
    },

    async editProject(project, model) {
        await axios.put(`/api/v0/projects/${project}`, model);
    },

    async deleteProject(project) {
        await axios.delete(`/api/v0/projects/${project}`);
    },

    async getUsers(id) {
        const { data: users } = await axios.get<ProjectUser[]>(`/api/v0/projects/${id}/users`);

        return users;
    },

    async getMilestones(id) {
        const { data: milestones } = await axios.get<MilestoneResponse[]>(
            `/api/v0/projects/${id}/milestones`
        );

        return milestones.map((m) => formatMilestone(m));
    },
    async createMilestone(id, name, startDate, endDate) {
        const { data: milestone } = await axios.post<MilestoneResponse>(
            `/api/v0/projects/${id}/milestones`,
            {
                name,
                startDate,
                endDate,
            }
        );

        return formatMilestone(milestone);
    },
    async editMilestone(project, milestone, patch) {
        const strMilestone = {
            id: milestone.id,
            name: milestone.name,
            startDate: milestone.startDate?.toISOString(),
            endDate: milestone.endDate?.toISOString(),
        };

        const edited = {
            ...strMilestone,
            ...patch,
        };

        const { compare } = await import("fast-json-patch");

        const data = compare(milestone, edited);

        await sendPatch(`/api/v0/projects/${project}/milestones/${milestone.id}`, data);
    },
    async deleteMilestone(project, milestone) {
        await axios.delete(`/api/v0/projects/${project}/milestones/${milestone}`);
    },

    async getTeams(id) {
        const { data: teams } = await axios.get<ProjectTeam[]>(`/api/v0/projects/${id}/teams`);

        return teams;
    },
    async addTeam(project, team) {
        await axios.put(`/api/v0/projects/${project}/teams/${team}`);
    },
    async removeTeam(project, team) {
        await axios.delete(`/api/v0/projects/${project}/teams/${team}`);
    },
    async setTeamPermissions(project, team, permissions) {
        await axios.put(`/api/v0/projects/${project}/teams/${team}/permissions`, { permissions });
    },

    async getRoleHolders(id) {
        const { data } = await axios.get<ProjectRoleHolders[]>(`/api/v0/projects/${id}/roles`);
        return data;
    },
    async setRoleHolders(id, holders) {
        const { data } = await axios.put<ProjectRoleHolders[]>(
            `/api/v0/projects/${id}/roles`,
            holders
        );
        return data;
    },
    async getVersions(id: ProjectId) {
        const { data: versions } = await axios.get<ProjectVersionResponse[]>(
            `/api/v0/projects/${id}/versions`
        );

        return versions.map((v) => formatVersion(v));
    },
    async createVersion(id, name, startDate, endDate, freezeDate) {
        const { data: version } = await axios.post<ProjectVersionResponse>(
            `/api/v0/projects/${id}/versions`,
            {
                name,
                startDate: startDate ? toDateOnly(startDate) : undefined,
                endDate: endDate ? toDateOnly(endDate) : undefined,
                freezeDate: freezeDate ? toDateOnly(freezeDate) : undefined,
            }
        );

        return formatVersion(version);
    },
    async editVersion(project, version, patch) {
        const stringedVersion = {
            id: version.id,
            name: version.name,
            startDate: version.startDate ? toDateOnly(version.startDate) : null,
            endDate: version.endDate ? toDateOnly(version.endDate) : null,
            freezeDate: version.freezeDate ? toDateOnly(version.freezeDate) : null,
            releaseDate: version.releaseDate ? toDateOnly(version.releaseDate) : null,
            theme: version.theme,
            notes: version.notes,
            hiddenFromRadar: version.hiddenFromRadar,
        };

        const edited = {
            ...stringedVersion,
            ...patch,
        };

        const { compare } = await import("fast-json-patch");

        const data = compare(stringedVersion, edited);

        await sendPatch(`/api/v0/projects/${project}/versions/${version.id}`, data);
    },
    async setVersionHiddenFromRadar(project, version, hidden) {
        // A targeted replace so the radar and its restore list can toggle a
        // version without loading its full edit form.
        await sendPatch(`/api/v0/projects/${project}/versions/${version}`, [
            { op: "replace", path: "/hiddenFromRadar", value: hidden },
        ]);
    },
    async releaseVersion(project, version, patch) {
        await axios.patch(`/api/v0/projects/${project}/versions/${version}/release`, patch);
    },
    async deleteVersion(project, version) {
        await axios.delete(`/api/v0/projects/${project}/versions/${version}`);
    },

    async importTickets(project, file) {
        return await sendFile<{ epicsCreated: number; ticketsCreated: number }>(
            `/api/v0/projects/${project}/tickets/import`,
            file
        );
    },
};
